import React, { useMemo, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import PageTitle from '../../../components/PageTitle/PageTitle';
import CardGrid from '../../../components/Layout2/CardGrid/CardGrid';
import { ASSETS_BASE_URL, DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL } from '../../../config';
import { useTranslation } from 'react-i18next';
import { useSite } from '../../../context/siteContext';
import { buildExternalLoginUrl } from '../../../utilities/externalDocs';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants';
import { resolveServiceOptions } from "../../../utilities/customerLocationSettings";
import { useOptions } from "../../../context/optionContext";
import SparesInsights from "../../../components/SparesInsights/SparesInsights";
import { toast } from "../../../context/ToastContext";
import { launchReach360 } from "../../../utilities/reach360Sso";
const Home = () => {
    const { t } = useTranslation();
    const { selectedSiteData } = useSite();
    const navigate = useNavigate();
    // Recommendations used to always render as a list below the tile row, which AMF (Shubham/Lauren)
    // reported as "too messy". Now it's a tile like the others, collapsed by default; clicking it
    // toggles the panel below open/closed. The count still loads even while collapsed, so the badge is
    // accurate before the user ever opens it — see SparesInsights's onCountChange.
    const [showRecommendations, setShowRecommendations] = useState(false);
    const [recommendationsCount, setRecommendationsCount] = useState(0);

    const handleTrainingNavigate = async () => {
        try {
            await launchReach360();
        } catch (error) {
            if (error?.response?.status === 403) {
                navigate("/training");
                return;
            }
            toast.trigger({
                message: error?.response?.data?.message || error?.message || "Unable to open Reach 360.",
                severity: error?.code === "POPUP_BLOCKED" ? "warning" : "error",
            });
        }
    };

    const handleDocumentationNavigate = () => {
        //   Internal documentation → SPA navigation
        if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL) {
            navigate('/documentation');
            return;
        }

        const username = selectedSiteData?.amf_method_username_ln;
        const password = selectedSiteData?.amf_method_password_ln;

        const loginUrl = buildExternalLoginUrl({
            templateUrl: DOCUMENT_EXTERNAL_URL,
            username,
            password,
        });

        //   Fallback to internal docs if URL build fails
        if (!loginUrl) {
            navigate('/documentation');
            return;
        }
    }
    const homeData = useMemo(() => ([
        { title: t('UI_TEXT.PARTS'), img: `${ASSETS_BASE_URL}/ImageBoxes/parts.jpg`, href: '/parts' },
        { title: t('LABELS.DOCUMENTATION'), img: `${ASSETS_BASE_URL}/ImageBoxes/documentation.jpg`, href: ROUTES.DOCUMENT, onClick: handleDocumentationNavigate },
        { title: t('LABELS.TRAINING'), img: `${ASSETS_BASE_URL}/ImageBoxes/training.jpg`, href: '/training', onClick: handleTrainingNavigate },
        { title: t('LABELS.SERVICE'), img: `${ASSETS_BASE_URL}/ImageBoxes/service.jpg`, href: '/service' },
        {
            title: t('LABELS.RECOMMENDATIONS'),
            img: `${ASSETS_BASE_URL}/ImageBoxes/recommendations.jpg`,
            badge: recommendationsCount,
            active: showRecommendations,
            onClick: () => setShowRecommendations((v) => !v),
        },
    ]), [navigate, selectedSiteData, t, recommendationsCount, showRecommendations]);

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <PageTitle title={t('LABELS.HOME')} />
            <CardGrid data={homeData} columns={5} />
            {/* SOW items D and E — RSPL-gap coverage and due-for-reorder prompts, now behind the
                Recommendations tile above instead of always rendering as a list on load. Renders
                nothing when the site has neither, rather than showing an empty state.

                THIS IS THE ACTIVE LAYOUT. These panels were originally added only to
                pages/Home/Home.js, which belongs to DefaultLayout; the deployment selects Layout2 via
                REACT_APP_DEFAULT_LAYOUT, so they never appeared. pages/Home/Home.js uses MenuCards, not
                this tile grid, so the Recommendations tile itself lives only here — but SparesInsights
                and the translation copy are shared, so the RSPL/"you" language update applies there
                too. */}
            <Box sx={{ width: '100%' }}>
                <SparesInsights panelOpen={showRecommendations} onCountChange={setRecommendationsCount} />
            </Box>
        </Box>
    );
};

export default Home;
